import { Form } from "react-bootstrap";

// A service-param value, as service schemas allow them.
export type ServiceParamValue = string | number | boolean;

export type ServiceParamValueFieldProps = {
  name: string;
  // The param's JSON-schema definition (an allowed_params entry).
  schema: Record<string, unknown>;
  value: ServiceParamValue | undefined;
  // Numbers are emitted as parsed — NaN when the input doesn't parse; the
  // caller decides how to store that.
  onChange: (value: ServiceParamValue) => void;
  isInvalid?: boolean;
  id?: string;
  placeholder?: string;
};

// Resolve enum values: either directly on schema.enum, or via $ref/$defs
// (Pydantic StrEnum schema pattern).
const resolveEnumValues = (
  schema: Record<string, unknown>
): Array<ServiceParamValue> | undefined => {
  if (Array.isArray(schema.enum)) {
    return schema.enum as Array<ServiceParamValue>;
  }
  if (schema.$ref && schema.$defs) {
    const refName = (schema.$ref as string).replace("#/$defs/", "");
    const refDef = (schema.$defs as Record<string, Record<string, unknown>>)[
      refName
    ];
    if (refDef && Array.isArray(refDef.enum)) {
      return refDef.enum as Array<ServiceParamValue>;
    }
  }
  return undefined;
};

// TODO(WEB-260 follow-up): migrate admin's ServiceAliasParamCard's
// hand-rolled copy of this field onto this component.
/**
 * The typed value input for one service param: an enum renders as a select,
 * strings and numbers as controls, booleans as True/False radios. Unhandled
 * schema types render nothing.
 */
const ServiceParamValueField = ({
  name,
  schema,
  value,
  onChange,
  isInvalid = false,
  id,
  placeholder,
}: ServiceParamValueFieldProps) => {
  const hasValue = value !== undefined && value !== null;

  const enumValues = resolveEnumValues(schema);
  if (Array.isArray(enumValues)) {
    return (
      <Form.Select
        id={id}
        name={name}
        value={hasValue ? String(value) : ""}
        required
        isInvalid={isInvalid}
        // Option values are strings; hand back the schema's own entry so
        // numeric and boolean enums keep their type.
        onChange={(e) =>
          onChange(
            enumValues.find((val) => String(val) === e.target.value) ??
              e.target.value
          )
        }
      >
        {!hasValue && <option value="">Select a value...</option>}
        {enumValues.map((val) => (
          <option key={String(val)} value={String(val)}>
            {String(val)}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (schema.type === "string") {
    return (
      <Form.Control
        id={id}
        name={name}
        value={hasValue ? (value as string) : ""}
        placeholder={placeholder}
        required
        isInvalid={isInvalid}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (schema.type === "number" || schema.type === "integer") {
    return (
      <Form.Control
        id={id}
        name={name}
        type="number"
        value={hasValue ? (value as number) : ""}
        placeholder={placeholder}
        required
        isInvalid={isInvalid}
        onChange={(e) =>
          onChange(
            schema.type === "integer"
              ? parseInt(e.target.value)
              : parseFloat(e.target.value)
          )
        }
      />
    );
  }

  if (schema.type === "boolean") {
    const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.value === "true");
    return (
      <span style={{ display: "flex", gap: "10px" }}>
        <Form.Check
          name={name}
          label="True"
          type="radio"
          checked={value === true}
          value="true"
          isInvalid={isInvalid}
          onChange={handleCheckChange}
        />
        <Form.Check
          name={name}
          label="False"
          type="radio"
          checked={value === false}
          value="false"
          isInvalid={isInvalid}
          onChange={handleCheckChange}
        />
      </span>
    );
  }

  return null;
};

export default ServiceParamValueField;
